import { useNavigate, useSearchParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Columns3, Plus, Search, SlidersHorizontal, X } from "lucide-react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useSellerOptionsQuery } from "@/modules/stores/services/queries/useSellerOptionsQuery";
import { useStoresQuery } from "@/shared/stores/services/queries/useStoresQuery";
import { formatDate } from "@/lib/formatting/date-time";
import { useHiddenColumns } from "@/shared/layout/table-columns";
import { useTableDensity } from "@/shared/layout/table-density";
import { StoreDetailsDrawer } from "@/modules/stores/components/StoreDetailsDrawer";
import { PageHeaderActions } from "@/shared/layout/PageHeaderActions";
import {
  Button,
  Checkbox,
  DataTable,
  ErrorState,
  Icon,
  Input,
  ItemCell,
  LoadingState,
  SearchSelect,
  SegmentedControl,
  StatTile,
  StatusDot,
  TablePagination,
  Tabs,
  Tag,
  TableEmpty,
  TileGrid,
  type Column,
  type TabItem,
} from "@/shared/rd";
import type { StoreKind, StoreListItem } from "@/lib/api/types";
import type { Locale } from "@/lib/i18n/config";

/**
 * The design's tabs read All stores / Active / Suspended. A store has no
 * status anywhere in the schema — no column, no table — so these are the same
 * three-tab shape over the one dimension that IS stored: the plan.
 */
type StorePlanTab = "all" | "pro" | "free";

type StoresPageProps = {
  locale: Locale;
  limit: number;
  offset: number;
  sellerId?: string;
  storeKind: StoreKind;
  /** `/stores/:id` (or `/restaurants/:id`): the record's big drawer is open over the list. */
  openStoreId?: string;
};

/**
 * The list's whole state lives in the URL — page, seller, search, plan tab
 * and the deactivated filter — so opening a store (a route of its own, which
 * remounts the page) and closing it again lands on the same list.
 */
type ListQuery = {
  limit: number;
  offset: number;
  sellerId?: string;
  q?: string;
  plan: StorePlanTab;
  deactivated: boolean;
};

function buildQuery(params: ListQuery) {
  const query = new URLSearchParams();
  query.set("limit", String(params.limit));
  query.set("offset", String(params.offset));
  if (params.sellerId) query.set("sellerId", params.sellerId);
  if (params.q) query.set("q", params.q);
  if (params.plan !== "all") query.set("plan", params.plan);
  if (params.deactivated) query.set("status", "deactivated");
  return query.toString();
}

function readPlan(value: string | null): StorePlanTab {
  return value === "pro" || value === "free" ? value : "all";
}

/** A filter that is applied: dark, readable, removable (the mock's chip). */
function AppliedFilter({ label, removeLabel, onRemove }: { label: string; removeLabel: string; onRemove: () => void }) {
  return (
    <button
      type="button"
      onClick={onRemove}
      aria-label={`${removeLabel}: ${label}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        height: 28,
        padding: "0 8px 0 12px",
        paddingInlineStart: 12,
        paddingInlineEnd: 8,
        border: 0,
        borderRadius: "var(--rd-radius-chip)",
        background: "var(--rd-text)",
        color: "var(--rd-surface)",
        fontSize: 13,
        fontWeight: 500,
        cursor: "pointer",
      }}
    >
      <span dir="auto">{label}</span>
      <Icon icon={X} size={14} />
    </button>
  );
}

/** A small popover anchored under its trigger; closes on Escape or outside click. */
function AnchoredPanel({
  open,
  onClose,
  trigger,
  children,
  width = 260,
}: {
  open: boolean;
  onClose: () => void;
  trigger: React.ReactNode;
  children: React.ReactNode;
  width?: number;
}) {
  const wrap = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(event.target as Node)) onClose();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);
  return (
    <div ref={wrap} style={{ position: "relative", display: "inline-flex" }}>
      {trigger}
      {open ? (
        <div
          style={{
            position: "absolute",
            insetInlineStart: 0,
            top: "calc(100% + 4px)",
            zIndex: 30,
            width,
            padding: 8,
            borderRadius: "var(--rd-radius-lg)",
            background: "var(--rd-surface)",
            boxShadow: "var(--rd-shadow-modal)",
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}

/** Focuses the first input inside it on mount (the picker in a just-opened panel). */
function AutoFocus({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    ref.current?.querySelector("input")?.focus();
  }, []);
  return <div ref={ref}>{children}</div>;
}

export function StoresPage({ locale, limit, offset, sellerId, storeKind, openStoreId }: StoresPageProps) {
  const t = useTranslations();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [filterOpen, setFilterOpen] = useState(false);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const search = searchParams.get("q")?.trim() ?? "";
  const plan = readPlan(searchParams.get("plan"));
  // Deactivated stores are hidden by default (2026-10-25); the toolbar offers
  // them back as a removable filter.
  const showDeactivated = searchParams.get("status") === "deactivated";
  const [queryInput, setQueryInput] = useState(search);
  const [compact, , setCompact] = useTableDensity();
  const [hiddenColumns, toggleColumn] = useHiddenColumns("stores");
  const {
    data: storesData,
    isLoading,
    error,
    refetch,
  } = useStoresQuery({
    limit,
    offset,
    sellerId,
    q: search || undefined,
    plan: plan === "all" ? undefined : plan,
    storeKind,
    status: showDeactivated ? "deactivated" : undefined,
  });
  const { data: sellersData } = useSellerOptionsQuery();

  const isRestaurants = storeKind === "RESTAURANT";
  const basePath = `/${locale}/admin/${isRestaurants ? "restaurants" : "stores"}`;
  const current: ListQuery = { limit, offset, sellerId, q: search || undefined, plan, deactivated: showDeactivated };
  const currentQuery = buildQuery(current);
  const listHref = `${basePath}?${currentQuery}`;
  const goTo = (changes: Partial<ListQuery>, options?: { replace?: boolean }) =>
    navigate(`${basePath}?${buildQuery({ ...current, ...changes })}`, options);

  // Typing searches after a pause; the search lands in the URL (replacing the
  // entry, not stacking one per keystroke) and restarts at page one.
  useEffect(() => {
    const next = queryInput.trim();
    if (next === search) return;
    const timer = setTimeout(() => goTo({ q: next || undefined, offset: 0 }, { replace: true }), 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-arm on typing only
  }, [queryInput]);

  const closeDrawer = useCallback(() => navigate(listHref), [navigate, listHref]);
  const leaveMissing = useCallback(() => navigate(listHref, { replace: true }), [navigate, listHref]);
  const listTitle = t(isRestaurants ? keys.admin.restaurantsTitle : keys.admin.onlineStoresTitle);
  const sellerOptions = useMemo(() => sellersData?.items ?? [], [sellersData?.items]);
  const activeSeller = sellerOptions.find((seller) => seller.id === sellerId);
  const closeFilter = () => setFilterOpen(false);

  const kindBadge = isRestaurants ? "servicebell" : "cart-world";
  const number = (value: number) => value.toLocaleString(locale === "ar" ? "ar-EG" : "en-US");
  const searchLabel = t(isRestaurants ? keys.admin.restaurantsSearch : keys.admin.storesSearch);

  const tabItems: TabItem<StorePlanTab>[] = [
    {
      value: "all",
      label: t(isRestaurants ? keys.admin.restaurantsTabAll : keys.admin.storesTabAll),
      count: storesData?.totalAll,
    },
    { value: "pro", label: t(keys.admin.storesPlanPro), count: storesData?.totalPremium },
    {
      value: "free",
      label: t(keys.admin.storesPlanFree),
      count: storesData ? storesData.totalAll - storesData.totalPremium : undefined,
    },
  ];

  const columns: Column<StoreListItem>[] = [
    {
      key: "store",
      label: t(keys.common.storeName),
      width: 2.2,
      render: (store) => (
        <ItemCell
          badge={store.logoUrl ? undefined : kindBadge}
          media={
            store.logoUrl ? (
              <img
                src={store.logoUrl}
                alt=""
                width={28}
                height={28}
                style={{ width: 28, height: 28, flex: "none", borderRadius: "var(--rd-radius-md)", objectFit: "cover" }}
              />
            ) : undefined
          }
          name={store.storeName}
          // A slug is an identifier: monospace, LTR, read back character by character.
          meta={
            <bdi dir="ltr" style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
              {store.storeSlug}
            </bdi>
          }
        />
      ),
    },
    {
      key: "seller",
      label: t(keys.common.seller),
      width: 1.6,
      render: (store) => <span dir="auto">{store.seller.name}</span>,
    },
    {
      key: "plan",
      label: t(keys.admin.storesColPlan),
      width: 0.8,
      render: (store) => (
        <Tag tone={store.isPremium ? "blue" : "grey"}>
          {t(store.isPremium ? keys.admin.storesPlanPro : keys.admin.storesPlanFree)}
        </Tag>
      ),
    },
    {
      key: "status",
      label: t(keys.storeControls.colStatus),
      width: 1,
      render: (store) =>
        store.deactivatedAt ? (
          <StatusDot tone="red">{t(keys.storeControls.statusDeactivated)}</StatusDot>
        ) : (
          <StatusDot tone="green">{t(keys.storeControls.statusLive)}</StatusDot>
        ),
    },
    {
      key: "created",
      label: t(keys.admin.storesColCreated),
      width: 1,
      align: "end",
      numeric: true,
      render: (store) => (
        <span style={{ color: "var(--rd-text-secondary)" }}>{formatDate(store.createdAt, locale)}</span>
      ),
    },
  ];

  const page = Math.floor(offset / limit) + 1;
  const statePage = t(isRestaurants ? "admin.restaurantsStatePage" : "admin.storesStatePage");
  const filtersActive = Boolean(search || sellerId || plan !== "all" || showDeactivated);
  const filterSummary = [
    search ? `“${search}”` : null,
    activeSeller ? `${t(keys.admin.storesFilterSeller)} ${t(keys.common.filterIs)} ${activeSeller.name}` : null,
    plan !== "all"
      ? `${t(keys.admin.storesColPlan)} ${t(keys.common.filterIs)} ${t(plan === "pro" ? keys.admin.storesPlanPro : keys.admin.storesPlanFree)}`
      : null,
    showDeactivated
      ? `${t(keys.storeControls.colStatus)} ${t(keys.common.filterIs)} ${t(keys.storeControls.filterDeactivated)}`
      : null,
  ]
    .filter(Boolean)
    .join(t("admin.storesFilterJoin"));
  const clearFilters = () => {
    setQueryInput("");
    navigate(`${basePath}?${buildQuery({ limit, offset: 0, plan: "all", deactivated: false })}`);
  };
  const createAction = () => navigate(isRestaurants ? `/${locale}/admin/restaurants/new` : `/${locale}/admin/sellers/new`);

  return (
    <>
      <PageHeaderActions>
        <Button
          variant="primary"
          size="sm"
          icon={Plus}
          onClick={createAction}
        >
          {t(isRestaurants ? keys.admin.restaurantCreateAction : keys.admin.onlineStoreCreateAction)}
        </Button>
      </PageHeaderActions>

      {/* The design's three tiles. Its middle one counts ACTIVE stores, which
          nothing in the schema records, so the plan fills the slot. */}
      <TileGrid min={200}>
        <StatTile
          badge={kindBadge}
          value={<span className="rd-num">{storesData ? number(storesData.totalAll) : "—"}</span>}
          label={t(isRestaurants ? keys.admin.restaurantsStatTotal : keys.admin.storesStatTotal)}
        />
        <StatTile
          badge={isRestaurants ? "menu" : "package"}
          value={
            <span className="rd-num">{storesData ? number(storesData.totalAll - storesData.totalPremium) : "—"}</span>
          }
          label={t(keys.admin.storesStatFree)}
        />
        <StatTile
          badge="star"
          value={<span className="rd-num">{storesData ? number(storesData.totalPremium) : "—"}</span>}
          label={t(keys.admin.storesPlanPro)}
        />
      </TileGrid>

      <Tabs tabs={tabItems} value={plan} onChange={(next) => goTo({ plan: next, offset: 0 })} label={listTitle} />

      <div className="rd-toolbar">
        <Input
          icon={Search}
          size="sm"
          type="search"
          value={queryInput}
          onChange={(event) => setQueryInput(event.target.value)}
          placeholder={searchLabel}
          aria-label={searchLabel}
          style={{ width: 260, maxWidth: "100%" }}
        />

        {/* The design's filter control: a dashed "Filter" that reads as "add
            one", and an applied filter as a chip you can read and remove. */}
        <AnchoredPanel
          open={filterOpen}
          onClose={closeFilter}
          trigger={
            <button
              type="button"
              aria-expanded={filterOpen}
              aria-haspopup="dialog"
              onClick={() => (filterOpen ? closeFilter() : setFilterOpen(true))}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                height: 28,
                padding: "0 10px",
                border: "1px dashed var(--rd-line-control-hover)",
                borderRadius: "var(--rd-radius-chip)",
                background: "transparent",
                font: "inherit",
                fontSize: 13,
                color: "var(--rd-text-secondary)",
                cursor: "pointer",
              }}
            >
              <Icon icon={SlidersHorizontal} size={14} />
              <span>{t(keys.common.filterAdd)}</span>
            </button>
          }
        >
          <span className="rd-field-label">{t(keys.admin.storesFilterSeller)}</span>
          <AutoFocus>
            <SearchSelect
              label={t(keys.admin.storesFilterSeller)}
              placeholder={t(keys.common.filterSearch)}
              emptyLabel={t(keys.common.noResults)}
              options={sellerOptions.map((seller) => ({ value: seller.id, label: seller.name }))}
              value={sellerId}
              onChange={(id) => {
                closeFilter();
                goTo({ offset: 0, sellerId: id });
              }}
            />
          </AutoFocus>
        </AnchoredPanel>

        {activeSeller ? (
          <AppliedFilter
            label={`${t(keys.admin.storesFilterSeller)} ${t(keys.common.filterIs)} ${activeSeller.name}`}
            removeLabel={t(keys.common.filterRemove)}
            onRemove={() => goTo({ offset: 0, sellerId: undefined })}
          />
        ) : null}

        {showDeactivated ? (
          <AppliedFilter
            label={`${t(keys.storeControls.colStatus)} ${t(keys.common.filterIs)} ${t(keys.storeControls.filterDeactivated)}`}
            removeLabel={t(keys.common.filterRemove)}
            onRemove={() => goTo({ offset: 0, deactivated: false })}
          />
        ) : (storesData?.totalDeactivated ?? 0) > 0 ? (
          <Button
            variant="ghost"
            size="sm"
            muted
            onClick={() => goTo({ offset: 0, deactivated: true })}
          >
            {t(keys.storeControls.filterDeactivatedCount, { count: storesData?.totalDeactivated ?? 0 })}
          </Button>
        ) : null}

        <div className="rd-spacer" style={{ flex: 1 }} />

        <SegmentedControl
          label={t(keys.common.rowDensity)}
          value={compact ? "compact" : "comfortable"}
          onChange={(value) => setCompact(value === "compact")}
          options={[
            { value: "comfortable", label: t(keys.common.densityComfortable) },
            { value: "compact", label: t(keys.common.densityCompact) },
          ]}
        />
        <AnchoredPanel
          open={columnsOpen}
          onClose={() => setColumnsOpen(false)}
          width={200}
          trigger={
            <Button
              variant="ghost"
              size="sm"
              muted
              icon={Columns3}
              aria-expanded={columnsOpen}
              onClick={() => setColumnsOpen((value) => !value)}
            >
              {t(keys.common.columns)}
            </Button>
          }
        >
          <Checkbox
            label={t(keys.common.seller)}
            checked={!hiddenColumns.has("seller")}
            onChange={() => toggleColumn("seller")}
          />
        </AnchoredPanel>
      </div>

      {isLoading ? (
        <LoadingState
          title={t(keys.shell.loadingTitle, { page: statePage })}
          sub={t(keys.shell.loadingSub)}
          rows={Math.min(limit, 6)}
        />
      ) : error || !storesData?.items ? (
        <ErrorState
          title={t(keys.shell.errorTitle, { page: statePage })}
          sub={t(keys.shell.errorSub)}
          onRetry={() => void refetch()}
          retryLabel={t(keys.shell.retry)}
        />
      ) : (
        <div className={compact ? "stores-compact" : undefined}>
          {compact ? (
            <style>{".stores-compact .rd-table-row:not(.rd-table-head){min-height:36px}.stores-compact .rd-cell{padding-block:3px}"}</style>
          ) : null}
          <DataTable
            label={listTitle}
            columns={columns.filter((column) => !hiddenColumns.has(column.key))}
            rows={storesData.items}
            rowKey={(store) => store.id}
            isDimmed={(store) => Boolean(store.deactivatedAt)}
            minWidth={720}
            onRowClick={(store) => navigate(`${basePath}/${store.id}?${currentQuery}`)}
            empty={
              filtersActive ? (
                <TableEmpty
                  illustration="admin-no-results"
                  title={t(isRestaurants ? "admin.restaurantsNoMatchTitle" : "admin.storesNoMatchTitle", { filters: filterSummary })}
                  actions={
                    <Button size="sm" icon={X} onClick={clearFilters}>
                      {t(keys.common.clearFilters)}
                    </Button>
                  }
                >
                  {t(isRestaurants ? "admin.restaurantsNoMatchText" : "admin.storesNoMatchText")}
                </TableEmpty>
              ) : (
                <TableEmpty
                  illustration="admin-empty-table"
                  title={t(isRestaurants ? "admin.restaurantsEmptyFirstTitle" : "admin.storesEmptyFirstTitle")}
                  actions={
                    <Button variant="primary" size="sm" icon={Plus} onClick={createAction}>
                      {t(isRestaurants ? keys.admin.restaurantCreateAction : keys.admin.onlineStoreCreateAction)}
                    </Button>
                  }
                >
                  {t(isRestaurants ? "admin.restaurantsEmptyFirstText" : "admin.storesEmptyFirstText")}
                </TableEmpty>
              )
            }
            footer={
              <TablePagination
                total={storesData.total}
                page={page}
                pageSize={limit}
                pageSizes={[10, 20, 50]}
                onPage={(next) => goTo({ offset: (next - 1) * limit })}
                onPageSize={(size) => goTo({ offset: 0, limit: size })}
                locale={locale}
                labels={{
                  range: (from, to, total) => t(keys.common.paginationSummary, { from, to, total }),
                  rows: t("admin.storesPagerRows"),
                  rowsPerPage: t("admin.storesPagerRowsPerPage"),
                  previous: t(keys.common.previous),
                  next: t(keys.common.next),
                  pages: t(keys.common.paginationNav),
                }}
              />
            }
          />
        </div>
      )}

      {openStoreId ? (
        <StoreDetailsDrawer
          locale={locale}
          storeId={openStoreId}
          listKind={storeKind}
          onClose={closeDrawer}
          onMissing={leaveMissing}
          onDeleted={leaveMissing}
        />
      ) : null}
    </>
  );
}
